import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { RiCheckLine, RiCloseLine } from "react-icons/ri";
import { zIndexes } from "@utils/zIndexLevel";
import {
  PAUSE_NOTIFY_CHIPS,
  buildCycleOutcomeView,
  saveCycleNotifyPause,
  saveCycleOutcomeAck,
  type CycleOutcomeKind,
  type CycleOutcomeTone,
} from "./cycleOutcome.model";

/**
 * 首頁快速問答
 * - sheet：過區間仍未預約、較晚預約回饋 → 底部浮層（portal + fixed，捲動也不離開視窗底）
 * - inline：提醒日／區間內／提早 → 內容區（瓦斯行上方）
 * - 未預約類可「暫停本次通知」並詢問原因（出國等）
 */

const C = {
  ink: "#1f2937",
  muted: "#8b909c",
};

/** 量測底部分頁列高度，讓浮層貼在其上緣（與 CoachPanel 同邏輯） */
const measureTabBarOffset = (): number => {
  const root = document.getElementById("root");
  if (!root) return 0;
  let best: HTMLElement | null = null;
  let bestArea = Infinity;
  root.querySelectorAll<HTMLElement>("div").forEach((el) => {
    const t = el.textContent || "";
    if (!(t.includes("首頁") && t.includes("訂單") && t.includes("帳號"))) return;
    const r = el.getBoundingClientRect();
    const area = r.width * r.height;
    if (area > 0 && area < bestArea) {
      best = el;
      bestArea = area;
    }
  });
  if (!best) return 0;
  const rect = (best as HTMLElement).getBoundingClientRect();
  return Math.max(0, Math.round(window.innerHeight - rect.top));
};

const TONE: Record<
  CycleOutcomeTone,
  { accent: string; soft: string; softFg: string }
> = {
  warn: { accent: "#D97706", soft: "#FEF3C7", softFg: "#92400E" },
  danger: { accent: "#DC2626", soft: "#FEE2E2", softFg: "#991B1B" },
  info: { accent: "#2563EB", soft: "#DBEAFE", softFg: "#1E40AF" },
};

export interface CycleOutcomeCardProps {
  kind: CycleOutcomeKind;
  contextId?: string | number | null;
  anchorDate: string;
  variant?: "sheet" | "inline";
  onDismissed?: () => void;
  /** 有傳則打後端；失敗時仍寫 localStorage 作 offline */
  onSubmitAnswer?: (chip: string, note?: string) => Promise<void>;
  onSubmitPause?: (reason: string, note?: string) => Promise<void>;
}

export const CycleOutcomeCard = ({
  kind,
  contextId,
  anchorDate,
  variant = "inline",
  onDismissed,
  onSubmitAnswer,
  onSubmitPause,
}: CycleOutcomeCardProps) => {
  const view = buildCycleOutcomeView(kind);
  const theme = TONE[view.tone];
  const [mode, setMode] = useState<"ask" | "pause" | "thanks">("ask");
  const [thanksLabel, setThanksLabel] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (mode !== "thanks") return;
    const t = window.setTimeout(() => {
      onDismissed?.();
    }, 1400);
    return () => window.clearTimeout(t);
  }, [mode, onDismissed]);

  const finishAsk = async (chip: string, text?: string) => {
    const trimmed = (text ?? note).trim();
    try {
      if (onSubmitAnswer) {
        await onSubmitAnswer(chip, trimmed || undefined);
      } else {
        saveCycleOutcomeAck(contextId, kind, anchorDate, chip, trimmed || undefined);
      }
    } catch {
      saveCycleOutcomeAck(contextId, kind, anchorDate, chip, trimmed || undefined);
    }
    const chipLabel = view.chips.find((c) => c.key === chip)?.label;
    setThanksLabel(trimmed || chipLabel || "已記錄");
    setMode("thanks");
  };

  const finishPause = async (reason: string, text?: string) => {
    const trimmed = (text ?? note).trim();
    try {
      if (onSubmitPause) {
        await onSubmitPause(reason, trimmed || undefined);
      } else {
        saveCycleNotifyPause(contextId, anchorDate, reason, trimmed || undefined);
        saveCycleOutcomeAck(contextId, kind, anchorDate, `pause:${reason}`, trimmed || undefined);
      }
    } catch {
      saveCycleNotifyPause(contextId, anchorDate, reason, trimmed || undefined);
      saveCycleOutcomeAck(contextId, kind, anchorDate, `pause:${reason}`, trimmed || undefined);
    }
    const reasonLabel = PAUSE_NOTIFY_CHIPS.find((c) => c.key === reason)?.label;
    setThanksLabel(
      trimmed
        ? `已暫停本輪通知・${trimmed}`
        : `已暫停本輪通知・${reasonLabel ?? reason}`,
    );
    setMode("thanks");
  };

  const canSubmitNote = note.trim().length > 0 || selected != null;

  const [tabBarOffset, setTabBarOffset] = useState(0);
  useEffect(() => {
    if (variant !== "sheet") return;
    const update = () => setTabBarOffset(measureTabBarOffset());
    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [variant]);

  const wrapSheet = (node: ReactNode) => {
    if (variant !== "sheet") return <>{node}</>;
    return createPortal(
      <div
        className="cycle-outcome-sheet-root"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: tabBarOffset,
          zIndex: zIndexes.sheetAbovePageFooter,
          padding: "0 12px 12px",
          pointerEvents: "none",
        }}>
        <style>{`
          @keyframes cycleOutcomeSheetIn {
            from { opacity: 0; transform: translateY(28px) scale(0.985); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .cycle-outcome-sheet-panel {
            pointer-events: auto;
            transform-origin: 50% 100%;
            animation: cycleOutcomeSheetIn 0.34s cubic-bezier(0.2, 0.9, 0.3, 1);
          }
          @media (prefers-reduced-motion: reduce) {
            .cycle-outcome-sheet-panel { animation: none; }
          }
        `}</style>
        <div className="cycle-outcome-sheet-panel">{node}</div>
      </div>,
      document.body,
    );
  };

  if (mode === "thanks") {
    return wrapSheet(
      <div
        style={{
          background: variant === "sheet" ? "#111827" : "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow:
            variant === "sheet"
              ? "0 10px 28px rgba(17,24,39,0.35)"
              : "0 4px 14px -8px rgba(31,41,55,0.2)",
          border: variant === "sheet" ? "none" : "1px solid #EAECF0",
          fontFamily: "inherit",
          color: variant === "sheet" ? "#fff" : C.ink,
        }}>
        <div style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: variant === "sheet" ? "#16A34A" : "#DCFCE7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
            <RiCheckLine size={16} color={variant === "sheet" ? "#fff" : "#16A34A"} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800 }}>已收到，謝謝</div>
            <div
              style={{
                fontSize: 12,
                opacity: variant === "sheet" ? 0.75 : 1,
                color: variant === "sheet" ? undefined : C.muted,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}>
              {thanksLabel}
            </div>
          </div>
        </div>
      </div>,
    );
  }

  const cardShell = (opts: {
    title: string;
    body: string;
    badge: string;
    chips: { key: string; label: string }[];
    onChip: (key: string) => void;
    onSubmit: () => void;
    onBack?: () => void;
    inputPlaceholder: string;
    footer?: ReactNode;
  }) => (
    <div
      style={{
        background: "#fff",
        borderRadius: variant === "sheet" ? 20 : 16,
        overflow: "hidden",
        boxShadow:
          variant === "sheet"
            ? "0 -4px 32px rgba(17,24,39,0.18), 0 8px 24px rgba(17,24,39,0.12)"
            : "0 6px 18px -10px rgba(31,41,55,0.22)",
        border: "1px solid #EAECF0",
        fontFamily: "inherit",
      }}>
      <div style={{ padding: "14px 14px 16px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {opts.badge !== "30秒問答" && (
              <span
                style={{
                  display: "inline-block",
                  fontSize: 11,
                  fontWeight: 800,
                  color: theme.softFg,
                  background: theme.soft,
                  padding: "3px 8px",
                  borderRadius: 999,
                  marginBottom: 6,
                }}>
                {opts.badge}
              </span>
            )}
            <div style={{ fontSize: 17, fontWeight: 800, color: C.ink, lineHeight: 1.3 }}>
              {opts.title}
            </div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4, lineHeight: 1.4 }}>
              {opts.body}
            </div>
          </div>
          <button
            type="button"
            aria-label="稍後再說"
            onClick={() => finishAsk("dismiss")}
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              border: "1px solid #E8ECF0",
              background: "#F7F8FA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flexShrink: 0,
              color: C.muted,
            }}>
            <RiCloseLine size={18} />
          </button>
        </div>

        {opts.onBack && (
          <button
            type="button"
            onClick={opts.onBack}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              fontSize: 12,
              fontWeight: 700,
              padding: "0 0 8px",
              cursor: "pointer",
              fontFamily: "inherit",
            }}>
            ← 回上一層
          </button>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {opts.chips.map((c) => {
            const on = selected === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => opts.onChip(c.key)}
                style={{
                  minHeight: 48,
                  padding: "11px 10px",
                  borderRadius: 14,
                  border: on ? `1.5px solid ${theme.accent}` : "1.5px solid #E5E9EF",
                  background: on ? theme.soft : "#F5F7FA",
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: on ? theme.softFg : C.ink,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  lineHeight: 1.25,
                  textAlign: "center",
                }}>
                {c.label}
              </button>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 6 }}>
            或補充說明
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && canSubmitNote) {
                  e.preventDefault();
                  opts.onSubmit();
                }
              }}
              placeholder={opts.inputPlaceholder}
              maxLength={80}
              style={{
                flex: 1,
                minWidth: 0,
                height: 44,
                padding: "0 12px",
                borderRadius: 12,
                border: "1.5px solid #E5E9EF",
                background: "#FAFBFC",
                fontSize: 13.5,
                fontFamily: "inherit",
                color: C.ink,
                outline: "none",
              }}
            />
            <button
              type="button"
              disabled={!canSubmitNote}
              onClick={opts.onSubmit}
              style={{
                flexShrink: 0,
                minWidth: 64,
                height: 44,
                padding: "0 14px",
                borderRadius: 12,
                border: "none",
                background: canSubmitNote ? theme.accent : "#E5E9EF",
                color: canSubmitNote ? "#fff" : C.muted,
                fontSize: 13.5,
                fontWeight: 800,
                fontFamily: "inherit",
                cursor: canSubmitNote ? "pointer" : "default",
              }}>
              送出
            </button>
          </div>
        </div>

        {opts.footer}
      </div>
    </div>
  );

  if (mode === "pause") {
    return wrapSheet(
      cardShell({
        badge: "暫停本輪通知",
        title: "為什麼要暫停？",
        body: "這一輪預測期間先不提醒；下次叫桶後會重新開始。",
        chips: PAUSE_NOTIFY_CHIPS,
        inputPlaceholder: "例如：出國到 8/5、家裡沒人…",
        onBack: () => {
          setSelected(null);
          setNote("");
          setMode("ask");
        },
        onChip: (key) => {
          if (note.trim()) {
            setSelected(key);
            return;
          }
          finishPause(key);
        },
        onSubmit: () => finishPause(selected ?? "other", note),
      }),
    );
  }

  return wrapSheet(
    cardShell({
      badge: view.badge,
      title: view.title,
      body: view.body,
      chips: view.chips,
      inputPlaceholder: view.inputPlaceholder,
      onChip: (key) => {
        // 選出國／外出 → 導向暫停本輪，再問多久
        if (view.showPauseNotify && (key === "abroad" || key === "away")) {
          setSelected(null);
          setNote("");
          setMode("pause");
          return;
        }
        if (note.trim()) {
          setSelected(key);
          return;
        }
        finishAsk(key);
      },
      onSubmit: () => finishAsk(selected ?? "note", note),
      footer: view.showPauseNotify ? (
        <button
          type="button"
          onClick={() => {
            setSelected(null);
            setNote("");
            setMode("pause");
          }}
          style={{
            marginTop: 12,
            width: "100%",
            height: 44,
            borderRadius: 12,
            border: "1.5px dashed #D1D5DB",
            background: "#FAFAFA",
            color: C.ink,
            fontSize: 13.5,
            fontWeight: 700,
            fontFamily: "inherit",
            cursor: "pointer",
          }}>
          暫停本次通知
        </button>
      ) : null,
    }),
  );
};
